import PropTypes from "prop-types";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import moment from "moment";
import menNurse from "../assets/common/menNurse.png";
import menPatient from "../assets/common/menPatient.png";
import { CommonImages } from "../reusableUI/images";

const GenderChart = ({ users, timePeriod }) => {
  const filterUsersByTimePeriod = (users, period) => {
    if (!users || !Array.isArray(users)) return [];

    const now = moment();
    let filteredUsers = [...users];

    switch (period) {
      case "today":
        filteredUsers = filteredUsers.filter((user) =>
          moment(user.createdAt).isSame(now, "day")
        );
        break;
      case "last7days":
        filteredUsers = filteredUsers.filter((user) =>
          moment(user.createdAt).isAfter(now.clone().subtract(7, "days"))
        );
        break;
      case "last30days":
        filteredUsers = filteredUsers.filter((user) =>
          moment(user.createdAt).isAfter(now.clone().subtract(30, "days"))
        );
        break;
      case "alltime":
        // Return all users for alltime
        break;
      default:
        filteredUsers = filteredUsers.filter((user) =>
          moment(user.createdAt).isSame(now, "day")
        );
        break;
    }

    return filteredUsers;
  };

  const filteredUsers = filterUsersByTimePeriod(users, timePeriod);

  let maleNurses = 0;
  let femaleNurses = 0;
  let malePatients = 0;
  let femalePatients = 0;
  let malePhysio = 0;
  let femalePhysio = 0;

  // console.log(filteredUsers);

  if (Array.isArray(filteredUsers)) {
    filteredUsers.forEach((user) => {
      if (user.role === "nurse") {
        if (user.gender === "male") {
          maleNurses++;
        } else if (user.gender === "female") {
          femaleNurses++;
        }
      } else if (user.role === "patient") {
        if (user.gender === "male") {
          malePatients++;
        } else if (user.gender === "female") {
          femalePatients++;
        }
      } else if (user.role === "physiotherapist") {
        if (user.gender === "male") {
          malePhysio++;
        } else if (user.gender === "female") {
          femalePhysio++;
        }
      }
    });
  } else {
    console.error("The users prop should be an array");
  }

  const totalMen = maleNurses + malePatients + malePhysio;
  const totalWomen = femaleNurses + femalePatients + femalePhysio;

  const maleNursePercentage =
    totalMen > 0 ? ((maleNurses / totalMen) * 100).toFixed(1) : 0;
  const malePatientPercentage =
    totalMen > 0 ? ((malePatients / totalMen) * 100).toFixed(1) : 0;
  const malePhysioPercentage =
    totalMen > 0 ? ((malePhysio / totalMen) * 100).toFixed(1) : 0;
  const femaleNursePercentage =
    totalWomen > 0 ? ((femaleNurses / totalWomen) * 100).toFixed(1) : 0;
  const femalePatientPercentage =
    totalWomen > 0 ? ((femalePatients / totalWomen) * 100).toFixed(1) : 0;
  const femalePhysioPercentage =
    totalWomen > 0 ? ((femalePhysio / totalWomen) * 100).toFixed(1) : 0;

  const optionsMen = {
    chart: {
      type: "pie",
      height: 120,
      width: 120,
    },
    title: {
      text: null,
    },
    plotOptions: {
      pie: {
        innerSize: "0%",
        dataLabels: {
          enabled: false,
          format: "{point.name}: {point.percentage:.1f} %",
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Users",
        data: [
          { name: "Patients", y: malePatients, color: "#9DAEF4" },
          { name: "Nurses", y: maleNurses, color: "#CEDCF6" },
          { name: "Pysiotherapist", y: malePhysio, color: "#4951EC" },
        ],
      },
    ],
  };

  const optionsWomen = {
    chart: {
      type: "pie",
      height: 120,
      width: 120,
    },
    title: {
      text: null,
    },
    plotOptions: {
      pie: {
        innerSize: "0%",
        dataLabels: {
          enabled: false,
          format: "{point.name}: {point.percentage:.1f} %",
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: [
      {
        name: "Users",
        data: [
          { name: "Patients", y: femalePatients, color: "#CEDCF6" },
          { name: "Nurses", y: femaleNurses, color: "#9DAEF4" },
          { name: "Pysiotherapist", y: femalePhysio, color: "#4951EC" },
        ],
      },
    ],
  };

  const hasData =
    maleNurses > 0 ||
    femaleNurses > 0 ||
    malePatients > 0 ||
    femalePatients > 0 ||
    malePhysio ||
    femalePhysio;

  return (
    <div className="bg-white rounded-xl h-[35vh] shadow-md">
      <div className="flex justify-between items-center">
        <h4 className="text-[14px] font-bold text-gray-800 px-4 pt-2 mb-2">
          Users By Gender
        </h4>
      </div>
      <div className="border-b border-gray-200 mb-3 mt-1"></div>
      {hasData ? (
        <div className="flex flex-row items-center justify-center mt-4">
          <div className="flex flex-row items-center ml-2">
            <div className="flex flex-col items-center mt-[0px] ml-8">
              <div className="flex flex-row items-center mb-2">
                <img src={menNurse} alt="Men Nurse" className="h-2 w-2" />
                <p className="text-[12px] w-[50px] ml-2">
                  Nurses {maleNursePercentage}%
                </p>
              </div>
              <div className="flex flex-row items-center mt-5">
                <img src={menPatient} alt="Men Patient" className="h-2 w-2" />
                <p className="text-[12px] ml-2 w-[55px]">
                  Patients {malePatientPercentage}%
                </p>
              </div>
              <div className="flex flex-row items-center mt-5">
                <img
                  src={CommonImages.playStore}
                  alt="Men Patient"
                  className="h-2 w-2"
                />
                <p className="text-[12px] ml-2 w-[55px]">
                  Physio {malePhysioPercentage}%
                </p>
              </div>
            </div>
            <div className="pr-4 flex flex-col items-center self-center">
              <HighchartsReact highcharts={Highcharts} options={optionsMen} />
              <div className="mb-3 text-[12px]">(Men)</div>
            </div>
          </div>
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-col  items-center mt-[0px]">
              <div className="flex flex-row items-center mb-2">
                <img src={menNurse} alt="Women Nurse" className="h-2 w-2" />
                <p className="text-[12px] w-[50px] ml-2">
                  Nurses {femaleNursePercentage}%
                </p>
              </div>
              <div className="flex flex-row items-center mt-5">
                <img
                  src={menPatient}
                  alt="Women Patient"
                  className="h-2 w-2 ml-1"
                />
                <p className="text-[12px] w-[55px] ml-2">
                  Patients {femalePatientPercentage}%
                </p>
              </div>
              <div className="flex flex-row items-center mt-5">
                <img
                  src={CommonImages.playStore}
                  alt="Women Patient"
                  className="h-2 w-2 ml-1"
                />
                <p className="text-[12px] w-[55px] ml-2">
                  Physio{femalePhysioPercentage}%
                </p>
              </div>
            </div>
            <div className="pr-6 flex flex-col items-center self-center">
              <HighchartsReact highcharts={Highcharts} options={optionsWomen} />
              <p className="text-[12px]">(Women)</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-48">
          <p className="text-gray-500 text-[14px]">
            No new registrations in selected period
          </p>
        </div>
      )}
    </div>
  );
};

GenderChart.propTypes = {
  users: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.string.isRequired,
      gender: PropTypes.string,
      createdAt: PropTypes.string,
    })
  ),
  timePeriod: PropTypes.oneOf(["today", "last7days", "last30days", "alltime"])
    .isRequired,
};

export default GenderChart;
